import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
    BookOpen,
    CalendarDays,
    Edit3,
    Feather,
    Heart,
    MessageCircle,
    Plus,
    Search,
    Sparkles,
    Tag,
    Trash2,
    X
} from 'lucide-react';
import ScreenLayout from './ScreenLayout';
import {
    addEntry,
    deleteEntry,
    getAllEntries,
    initializeJournal,
    updateEntry
} from '../services/journalService';
import { getCalendarEvents, toDateKey } from '../services/prayerCalendarService';

const blankDraft = (reflection) => ({
    title: reflection?.title || '',
    content: '',
    relatedScripture: reflection?.relatedScripture || '',
    tags: ['daily reflection']
});

const prompts = [
    { label: 'Gratitude', icon: Sparkles, title: 'Three gifts from today', starter: 'Today I am grateful for…', tag: 'gratitude' },
    { label: 'Prayer', icon: Heart, title: 'What I am carrying', starter: 'God, today I want to bring you…', tag: 'prayer' },
    { label: 'Reflection', icon: Feather, title: 'What stood out today', starter: 'One thing I want to remember from today is…', tag: 'reflection' }
];

const formatDate = (date) => new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
}).format(new Date(date));

const Journal = () => {
    const location = useLocation();
    // A reading is a starting point for this visit. Subsequent renders must not
    // replace the reader's own words or reapply the title after saving.
    const [reflection] = useState(() => location.state?.reflection || null);
    const [entries, setEntries] = useState([]);
    const [draft, setDraft] = useState(() => blankDraft(reflection));
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState('');
    const [activeTag, setActiveTag] = useState('');
    const [showArchive, setShowArchive] = useState(false);
    const [saved, setSaved] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        initializeJournal();
        setEntries(getAllEntries());
    }, []);

    const todayEvents = useMemo(
        () => getCalendarEvents().filter((event) => event.date === toDateKey(new Date())),
        []
    );
    const tags = useMemo(
        () => [...new Set(entries.flatMap((entry) => entry.tags || []))].sort(),
        [entries]
    );
    const filteredEntries = useMemo(() => {
        const query = search.trim().toLowerCase();
        return entries.filter((entry) => {
            const matchesTag = !activeTag || entry.tags?.includes(activeTag);
            const matchesSearch = !query || [entry.title, entry.content, entry.relatedScripture, ...(entry.tags || [])]
                .some((value) => String(value || '').toLowerCase().includes(query));
            return matchesTag && matchesSearch;
        });
    }, [activeTag, entries, search]);
    const visibleEntries = showArchive || search || activeTag ? filteredEntries : filteredEntries.slice(0, 5);
    const returnPath = typeof reflection?.sourcePath === 'string' && /^\/(?!\/)/.test(reflection.sourcePath)
        ? reflection.sourcePath
        : '/devotionals';

    const focusComposer = () => {
        document.getElementById('journal-composer')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        contentRef.current?.focus({ preventScroll: true });
    };

    const changeDraft = (field, value) => {
        setSaved(false);
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const resetDraft = () => {
        setEditingId(null);
        setDraft(blankDraft());
        setSaved(false);
    };

    const applyPrompt = (prompt) => {
        setEditingId(null);
        setSaved(false);
        setDraft({
            title: prompt.title,
            content: `${prompt.starter}\n\n`,
            relatedScripture: '',
            tags: ['daily reflection', prompt.tag]
        });
        focusComposer();
    };

    const applyCalendarPrompt = (event) => {
        setEditingId(null);
        setSaved(false);
        setDraft({
            title: event.title,
            content: `${event.description}\n\nMy prayer and reflection:\n`,
            relatedScripture: event.scripture || '',
            tags: ['daily reflection', 'prayer plan']
        });
        focusComposer();
    };

    const saveDraft = (event) => {
        event.preventDefault();
        if (!draft.content.trim()) return;
        const payload = {
            ...draft,
            title: draft.title.trim() || `Reflection for ${formatDate(new Date())}`
        };
        if (editingId) updateEntry(editingId, payload);
        else addEntry(payload);
        setEntries(getAllEntries());
        setDraft(blankDraft());
        setEditingId(null);
        setSaved(true);
    };

    const editEntry = (entry) => {
        setEditingId(entry.id);
        setSaved(false);
        setDraft({
            title: entry.title || '',
            content: entry.content || '',
            relatedScripture: entry.relatedScripture || '',
            tags: entry.tags || []
        });
        focusComposer();
    };

    const removeEntry = (entryId) => {
        if (!window.confirm('Delete this journal entry?')) return;
        deleteEntry(entryId);
        setEntries(getAllEntries());
        if (editingId === entryId) resetDraft();
    };

    const addTag = (tag) => {
        const normalized = tag.trim().toLowerCase();
        if (normalized && !draft.tags.includes(normalized)) changeDraft('tags', [...draft.tags, normalized]);
    };

    return (
        <ScreenLayout title="Prayer Journal" icon={MessageCircle} className="journal-detail">
            <header className="detail-heading">
                <p className="detail-date">{formatDate(new Date())}</p>
                <h1>A quiet place for what is on your heart.</h1>
                <p className="detail-subtitle">Write a sentence or fill a page. Make a little space for what matters today.</p>
            </header>

            <div className="detail-grid">
                <section id="journal-composer" className="detail-paper" aria-labelledby="journal-composer-heading">
                    <header className="detail-section">
                        <p className="shared-kicker">{editingId ? 'Editing an entry' : 'Today’s page'}</p>
                        <h2 className="detail-section-title" id="journal-composer-heading">{editingId ? 'Revise what you wrote' : 'Begin wherever you are'}</h2>
                        {reflection && (
                            <p className="journal-reading-context">
                                Reflecting on {reflection.title || 'your reading'}.{' '}
                                <Link to={returnPath}>Return to {reflection.sourceLabel || 'the reading'}</Link>
                            </p>
                        )}
                    </header>

                    {todayEvents.length > 0 && (
                        <section className="detail-section journal-today-plan">
                            <p className="shared-kicker">On today’s prayer calendar</p>
                            <h3 className="detail-section-title">{todayEvents[0].title}</h3>
                            <p>{todayEvents[0].description}</p>
                            {todayEvents[0].scripture && <p>{todayEvents[0].scripture}</p>}
                            <button className="detail-button detail-button-secondary" type="button" onClick={() => applyCalendarPrompt(todayEvents[0])}>Reflect on this today <Plus size={16} aria-hidden="true" /></button>
                        </section>
                    )}

                    <form className="detail-form" onSubmit={saveDraft}>
                        <label className="detail-field">
                            <span>Title <small>(optional)</small></span>
                            <input
                                value={draft.title}
                                onChange={(event) => changeDraft('title', event.target.value)}
                                placeholder="Give today’s thought a title, or leave this blank"
                            />
                        </label>
                        <label className="detail-field">
                            <span>Your reflection</span>
                            <textarea
                                ref={contentRef}
                                rows="10"
                                value={draft.content}
                                onChange={(event) => changeDraft('content', event.target.value)}
                                placeholder="What are you noticing, carrying, praying, or learning today?"
                            />
                        </label>
                        <label className="detail-field">
                            <span>Related Scripture <small>(optional)</small></span>
                            <input
                                value={draft.relatedScripture}
                                onChange={(event) => changeDraft('relatedScripture', event.target.value)}
                                placeholder="A verse or passage to keep close"
                            />
                        </label>
                        <div className="detail-field">
                            <span id="journal-tags-label">Tags</span>
                            <div className="journal-draft-tags" role="group" aria-labelledby="journal-tags-label">
                                {draft.tags.map((tag) => (
                                    <button type="button" key={tag} aria-label={`Remove ${tag} tag`} onClick={() => changeDraft('tags', draft.tags.filter((item) => item !== tag))}>{tag}<X size={12} aria-hidden="true" /></button>
                                ))}
                            </div>
                            <div className="journal-quick-tags" role="group" aria-label="Add a tag">
                                {['answered prayer', 'gratitude', 'family'].filter((tag) => !draft.tags.includes(tag)).map((tag) => (
                                    <button type="button" key={tag} aria-label={`Add ${tag} tag`} onClick={() => addTag(tag)}><Plus size={13} aria-hidden="true" /> {tag}</button>
                                ))}
                            </div>
                        </div>
                        <div className="detail-actions">
                            <button className="detail-button" type="submit" disabled={!draft.content.trim()}>
                                <Feather size={17} aria-hidden="true" /> {editingId ? 'Update entry' : 'Keep this reflection'}
                            </button>
                            {editingId && <button className="detail-button detail-button-secondary" type="button" onClick={resetDraft}><X size={17} aria-hidden="true" /> Cancel edit</button>}
                        </div>
                        {saved && <p className="detail-status" role="status">Saved to your journal.</p>}
                    </form>

                    <section className="detail-section" aria-labelledby="journal-prompts-heading">
                        <h3 className="detail-section-title" id="journal-prompts-heading">Need a beginning?</h3>
                        <p>Choose a gentle prompt to start a new page.</p>
                        <div className="detail-actions">
                            {prompts.map((prompt) => (
                                <button className="detail-button detail-button-secondary" type="button" key={prompt.label} title={prompt.starter} onClick={() => applyPrompt(prompt)}>
                                    <prompt.icon size={17} aria-hidden="true" /> {prompt.label}
                                </button>
                            ))}
                        </div>
                    </section>
                </section>

                <aside className="detail-rail" aria-label="Your journal archive">
                    <section className="detail-rail-section">
                        <header className="journal-archive-header">
                            <div><p className="shared-kicker">Your collection</p><h2 className="detail-section-title">{showArchive ? 'All pages' : 'Recent pages'}</h2></div>
                            <button type="button" onClick={() => { setShowArchive(!showArchive); setSearch(''); setActiveTag(''); }}>{showArchive ? 'Show recent' : 'View all'}</button>
                        </header>
                        <label className="detail-field journal-search">
                            <span>Search your journal</span>
                            <span><Search size={17} aria-hidden="true" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="A word, verse, or thought" /></span>
                        </label>
                        {tags.length > 0 && (
                            <div className="journal-tag-filter" role="group" aria-label="Filter journal by tag">
                                <button type="button" aria-pressed={!activeTag} className={!activeTag ? 'is-active' : ''} onClick={() => setActiveTag('')}>All</button>
                                {tags.slice(0, 8).map((tag) => <button type="button" aria-pressed={activeTag === tag} className={activeTag === tag ? 'is-active' : ''} key={tag} onClick={() => setActiveTag(tag)}>{tag}</button>)}
                            </div>
                        )}
                        <div className="journal-entry-list">
                            {visibleEntries.map((entry) => (
                                <article className="journal-entry-card" key={entry.id} aria-labelledby={`journal-entry-${entry.id}`}>
                                    <small>{formatDate(entry.date)}</small>
                                    <h3 id={`journal-entry-${entry.id}`}>{entry.title}</h3>
                                    <p>{entry.content}</p>
                                    {entry.relatedScripture && <span><BookOpen size={13} aria-hidden="true" /> {entry.relatedScripture}</span>}
                                    <div>{entry.tags?.slice(0, 3).map((tag) => <button type="button" key={tag} aria-label={`Show ${tag} entries`} onClick={() => { setShowArchive(true); setActiveTag(tag); }}><Tag size={11} aria-hidden="true" /> {tag}</button>)}</div>
                                    <footer>
                                        <button type="button" aria-label={`Edit ${entry.title}`} onClick={() => editEntry(entry)}><Edit3 size={15} aria-hidden="true" /> Edit</button>
                                        <button type="button" aria-label={`Delete ${entry.title}`} onClick={() => removeEntry(entry.id)}><Trash2 size={15} aria-hidden="true" /> Delete</button>
                                    </footer>
                                </article>
                            ))}
                            {!visibleEntries.length && <div className="journal-empty"><MessageCircle size={24} aria-hidden="true" /><p>{search || activeTag ? 'No pages match your search. Try another word or tag.' : 'Your first honest sentence can begin here.'}</p></div>}
                        </div>
                    </section>
                    <section className="detail-rail-section">
                        <Link className="journal-calendar-link" to="/practice/prayer-calendar"><CalendarDays size={18} aria-hidden="true" /><span><strong>Plan your prayer rhythm</strong><small>Add prompts to your prayer calendar</small></span></Link>
                    </section>
                </aside>
            </div>
        </ScreenLayout>
    );
};

export default Journal;
